import { useState, useEffect } from 'react';
import debounce from 'lodash.debounce';

import { getErrorString } from '../../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../../util/sentry';
import CART_ITEM from '../../../constants/cartItems';
import { CUSTOM_PRINT_TYPE } from '../../../../../constants/pro-custom-prints';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../constants/account-referrals';
import { cartHasSubscriptionItems } from '../helpers';

/**
 * Checkout cart hook.
 *
 * @param {Object} opts The options.
 * @param {Function} opts.mutation The checkout mutation.
 * @param {Function} opts.onSuccess Success callback.
 * @param {Function} opts.onError Error callback.
 * @param {Function} [opts.refetchPricing] Refetch pricing.
 * @param {boolean} [opts.loadingPricing] Whether pricing is loading,.
 * @param {Error} [opts.errorFetchingPricing] Whether pricing has errorer out.
 * @param {Function} opts.openAlert Open error modal action.
 * @param {Object} opts.pricing The pricing.
 * @param {Array} opts.cart The current cart
 * @param {Array} opts.checkoutItems The parsed checkout items
 * @returns {{onCheckout: onCheckout, submitting: boolean, checkoutError: string, success: boolean}}
 */
const useCheckoutCart = ({
  mutation,
  loadingPricing,
  pricing,
  cart,
  checkoutItems,
  refetchPricing = () => {},
  errorFetchingPricing,
  openAlert,
  onSuccess,
  onError,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [success, setSuccess] = useState(false);
  const [allowedPromoCodes, setAllowedPromoCodes] = useState([]);
  const debouncedRefetch = debounce(refetchPricing, 500);

  useEffect(() => {
    // If for whatever reason the pricing fails to load attempt to refetch it.
    if (!loadingPricing && !pricing && !errorFetchingPricing) {
      debouncedRefetch?.();

      sentryCaptureMessage('[Checkout Service] :: Could not load price', {
        level: 'error',
      });
    }

    if (errorFetchingPricing) {
      openAlert({
        message:
          'There was an error calculating your price, please try again in a while.',
        info:
          errorFetchingPricing?.request_id ||
          getErrorString(errorFetchingPricing),
      });

      sentryCaptureMessage(
        errorFetchingPricing?.message ??
          '[Checkout Service] :: Error fetching price',
        {
          level: 'error',
          error: errorFetchingPricing,
        },
      );
    }
  }, [errorFetchingPricing, loadingPricing]);

  useEffect(() => {
    const allowedCodes = [];
    if (
      (checkoutItems?.digital_bundle_items ?? []).length > 0 ||
      (checkoutItems?.photo_download_items ?? []).length > 0 ||
      !!(checkoutItems?.photo_items ?? []).find(
        i => !!i.digital_downloads_purchase,
      )
    ) {
      allowedCodes.push(ACCOUNT_REFERRAL_TYPE.DIGITAL_DOWNLOADS);
    }

    if (
      (checkoutItems?.photo_items ?? []).length > 0 ||
      //   We can't rely on checking the checkoutItems for custom_prints since
      //   their formatting is the same for Waldograms or actual custom prints.
      //   therefore, we rely on checking the custom prints & finding one that
      //   is NOT a Waldogram via the cart.
      cart.find(
        item =>
          [
            CART_ITEM.PRINT_PRODUCT,
            CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT,
          ].includes(item.type) ||
          (item.type === CART_ITEM.ALBUM_CUSTOM_PRINT &&
            [
              CUSTOM_PRINT_TYPE.CUSTOM_PRINT,
              CUSTOM_PRINT_TYPE.PRINT_PRODUCT,
            ].includes(item.albumCustomPrint?.custom_print?.type)),
      )
    ) {
      allowedCodes.push(ACCOUNT_REFERRAL_TYPE.PRINTS);
    }

    if (
      cart.find(
        item =>
          item.type === CART_ITEM.ALBUM_CUSTOM_PRINT &&
          item.albumCustomPrint?.custom_print?.type ===
            CUSTOM_PRINT_TYPE.WALDOGRAM,
      )
    ) {
      allowedCodes.push(
        ACCOUNT_REFERRAL_TYPE.WALDOGRAM_PROMO,
        ACCOUNT_REFERRAL_TYPE.TIER_1_WALDOGRAM_PROMO,
        ACCOUNT_REFERRAL_TYPE.ONE_FREE_WALDOGRAM,
      );
    }

    if (cartHasSubscriptionItems(cart)) {
      allowedCodes.push(ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS);
    }

    // An "All Products" code discounts digital downloads, prints and
    // subscriptions on the backend, so it applies whenever any of them do.
    if (
      [
        ACCOUNT_REFERRAL_TYPE.DIGITAL_DOWNLOADS,
        ACCOUNT_REFERRAL_TYPE.PRINTS,
        ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS,
      ].some(type => allowedCodes.includes(type))
    ) {
      allowedCodes.push(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS);
    }

    setAllowedPromoCodes(allowedCodes);
  }, [cart, checkoutItems]);

  const onCheckout = async ({
    id: stripeTokenId,
    email,
    stripe_payment_method_id: stripePaymentMethodId,
    stripe_connect_account_id: stripeConnectAccountId,
    promo_code,
  }) => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setCheckoutError('');

    try {
      const response = await mutation({
        stripeTokenId,
        email,
        promoCode: promo_code,
        stripePaymentMethodId,
        stripeConnectAccountId,
      });

      setSubmitting(false);
      setSuccess(true);
      onSuccess?.(response);
    } catch (error) {
      setCheckoutError(error);
      onError?.(error);
      sentryCaptureMessage(error, { level: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    submitting,
    checkoutError,
    success,
    onCheckout,
    allowedPromoCodes,
  };
};

export default useCheckoutCart;
